import React from 'react';
import { Box, Container, Typography, Button, Card } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { 
  Crown, 
  User, 
  Star,
  BarChart3,
  Award,
  Users,
  ArrowLeft,
  Clock,
  Sparkles,
  Shield
} from 'lucide-react';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import CatchError from '@/components/system/CacthError';

// 呼吸灯动画
const breatheAnimation = keyframes`
  0% {
    box-shadow: 0 0 20px rgba(138, 75, 175, 0.3);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 40px rgba(138, 75, 175, 0.6);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 20px rgba(138, 75, 175, 0.3);
    transform: scale(1);
  }
`;

// 皇冠旋转动画
const crownRotate = keyframes`
  0% {
    transform: translateY(0px) rotate(-2deg);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-10px) rotate(2deg);
    opacity: 1;
  }
  100% {
    transform: translateY(0px) rotate(-2deg);
    opacity: 0.8;
  }
`;

// 星星闪烁动画
const starTwinkle = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
`;

// 闪烁动画
const sparkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

// 主容器
const MainContainer = styled(Box)(({ theme }) => ({
  minHeight: 'calc(100vh - 120px)',
  background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)',
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 80%, rgba(138, 75, 175, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(91, 62, 236, 0.1) 0%, transparent 50%)',
    zIndex: 0,
  },
}));

// 内容容器
const ContentContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  textAlign: 'center',
  maxWidth: '700px',
  padding: theme.spacing(4),
}));

// 皇冠容器
const CrownContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  position: 'relative',
  
  '& .crown-icon': {
    width: '120px',
    height: '120px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(45deg, rgba(255, 215, 0, 0.2), rgba(138, 75, 175, 0.2))',
    borderRadius: '50%',
    border: '3px solid rgba(255, 215, 0, 0.4)',
    animation: `${crownRotate} 4s infinite ease-in-out`,
    color: '#FFD700',
    
    '&:hover': {
      animation: `${breatheAnimation} 2s infinite ease-in-out`,
    },
  },
  
  '& .star': {
    position: 'absolute',
    color: '#FFD700',
    animation: `${starTwinkle} 2s infinite ease-in-out`,
    
    '&.star-1': {
      top: '10%',
      left: '20%',
      animationDelay: '0s',
    },
    '&.star-2': {
      top: '15%',
      right: '25%',
      animationDelay: '0.7s',
    },
    '&.star-3': {
      bottom: '20%',
      left: '15%',
      animationDelay: '1.4s',
    },
    '&.star-4': {
      bottom: '15%',
      right: '20%',
      animationDelay: '2.1s',
    },
  },
  
  [theme.breakpoints.down('sm')]: {
    '& .crown-icon': {
      width: '100px',
      height: '100px',
    },
  },
}));

// 功能图标容器
const FeatureIconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  flexWrap: 'wrap',
  
  '& .feature-icon': {
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    border: '2px solid rgba(138, 75, 175, 0.3)',
    transition: 'all 0.3s ease',
    color: 'rgba(255, 255, 255, 0.8)',
    
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.15)',
      border: '2px solid rgba(138, 75, 175, 0.5)',
      transform: 'scale(1.1)',
    },
  },
  
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1.5),
    '& .feature-icon': {
      width: '50px',
      height: '50px',
    },
  },
}));

// 主标题
const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: '4rem',
  fontWeight: 900,
  background: 'linear-gradient(45deg, #FFD700, #8a4baf)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(2),
  letterSpacing: '-0.02em',
  
  [theme.breakpoints.down('md')]: {
    fontSize: '2.8rem',
  },
}));

// 副标题
const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.6rem',
  color: 'rgba(255, 255, 255, 0.8)',
  marginBottom: theme.spacing(1),
  fontWeight: 300,
  
  [theme.breakpoints.down('md')]: {
    fontSize: '1.3rem',
  },
}));

// 描述文字
const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: 'rgba(255, 255, 255, 0.6)',
  marginBottom: theme.spacing(4),
  lineHeight: 1.6,
}));

// 功能列表
const FeatureList = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(4),
  
  '& .feature-item': {
    background: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '12px',
    padding: theme.spacing(2),
    border: '1px solid rgba(138, 75, 175, 0.2)',
    transition: 'all 0.3s ease',
    
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(138, 75, 175, 0.4)',
    },
  },
}));

// 状态卡片
const StatusCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(138, 75, 175, 0.2)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  
  '&:hover': {
    animation: `${breatheAnimation} 2s infinite ease-in-out`,
  },
}));

// 返回按钮
const BackButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8a4baf, #5b3eec)',
  color: '#ffffff',
  padding: '12px 32px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '50px',
  textTransform: 'none',
  boxShadow: '0 8px 32px rgba(138, 75, 175, 0.3)',
  transition: 'all 0.3s ease',
  
  '&:hover': {
    background: 'linear-gradient(45deg, #9d5bc2, #6b4ef5)',
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(138, 75, 175, 0.4)',
  },
}));

// 闪烁装饰
const SparkleIcon = styled(Sparkles)(({ theme }) => ({
  position: 'absolute',
  color: 'rgba(138, 75, 175, 0.6)',
  animation: `${sparkle} 2s infinite ease-in-out`,
  
  '&.sparkle-1': {
    top: '10%',
    left: '15%',
    animationDelay: '0s',
  },
  '&.sparkle-2': {
    top: '20%',
    right: '10%',
    animationDelay: '1s',
  },
  '&.sparkle-3': {
    bottom: '15%',
    left: '25%',
    animationDelay: '2s',
  },
}));

const ReputationComingSoon: React.FC = () => {
  const router = useRouter();
  const { t } = useTranslation();

  const featureIcons = [
    { icon: <User size={20} />, name: '身份彰显' },
    { icon: <Star size={20} />, name: '公民等级' },
    { icon: <BarChart3 size={20} />, name: '游戏战绩' },
    { icon: <Award size={20} />, name: '稀有徽章' },
    { icon: <Users size={20} />, name: '权力社交' },
  ];

  const features = [
    '独特身份标识系统',
    '公民等级进阶体系',
    '全方位游戏战绩记录',
    '限量稀有徽章收集',
  ];

  return (
    <>
      <Head>
        <title>Reputation - Coming Soon | WoW.Space</title>
        <meta
          name="description"
          content="声望殿堂即将开启，展示您的身份地位和社交影响力！"
        />
      </Head>
      
      <MainContainer>
        {/* 闪烁装饰 */}
        <SparkleIcon className="sparkle-1" size={16} />
        <SparkleIcon className="sparkle-2" size={20} />
        <SparkleIcon className="sparkle-3" size={18} />
        
        <Container maxWidth="md">
          <ContentContainer>
            {/* 皇冠图标 */}
            <CrownContainer>
              <Box className="crown-icon">
                <Crown size={60} />
              </Box>
              <Star className="star star-1" size={12} />
              <Star className="star star-2" size={16} />
              <Star className="star star-3" size={14} />
              <Star className="star star-4" size={18} />
            </CrownContainer>

            {/* 主要内容 */}
            <MainTitle variant="h1">
              Reputation
            </MainTitle>
            
            <SubTitle>
              声望殿堂
            </SubTitle>
            
            <Description>
              无形成就转化为可见荣耀<br />
              最硬核的社交资本展示平台<br />
              彰显您在 WoW.Space 的地位与影响力
            </Description>

            {/* 功能图标 */}
            <FeatureIconsContainer>
              {featureIcons.map((feature, index) => (
                <Box key={index} className="feature-icon" title={feature.name}>
                  {feature.icon}
                </Box>
              ))}
            </FeatureIconsContainer>

            {/* 功能预览 */}
            <FeatureList>
              {features.map((feature, index) => (
                <Box key={index} className="feature-item">
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </FeatureList>

            {/* 状态卡片 */}
            <StatusCard>
              <Clock size={24} color="rgba(138, 75, 175, 0.8)" />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: 600 
                }}
              >
                Coming Soon
              </Typography>
              <Shield size={24} color="rgba(255, 215, 0, 0.8)" />
            </StatusCard>
          </ContentContainer>
        </Container>
      </MainContainer>
    </>
  );
};

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
}

export default CatchError(ReputationComingSoon);
